import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { FlaskConical, Copy, ExternalLink, RefreshCw, Trash2, Check, AlertCircle } from 'lucide-react';

const API_BASE = 'https://workvanapp.com';

export default function TestAccounts({ adminEmail }) {
  const [email, setEmail] = useState('test@workvanapp.com');
  const [password, setPassword] = useState('TestUser123!');
  const [affiliateRef, setAffiliateRef] = useState('');
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [testAccounts, setTestAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Load active test accounts
  const fetchTestAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const { data: companies, error: err } = await supabase
        .from('companies')
        .select('id, name, tier, subscription_status, created_at, owner_user_id, business_type')
        .eq('is_demo', true)
        .order('created_at', { ascending: false });

      if (err) throw err;

      // Get owner emails
      if (companies?.length > 0) {
        const ownerIds = [...new Set(companies.map(c => c.owner_user_id).filter(Boolean))];
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, email, full_name')
          .in('id', ownerIds);

        const profileMap = {};
        profiles?.forEach(p => { profileMap[p.id] = p; });

        setTestAccounts(companies.map(c => ({
          ...c,
          ownerEmail: profileMap[c.owner_user_id]?.email || 'unknown',
          ownerName: profileMap[c.owner_user_id]?.full_name || ''
        })));
      } else {
        setTestAccounts([]);
      }
    } catch (err) {
      console.error('Failed to fetch test accounts:', err);
    } finally {
      setLoadingAccounts(false);
    }
  };

  useEffect(() => { fetchTestAccounts(); }, []);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_BASE}/api/test-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          adminEmail,
          affiliateRef: affiliateRef || undefined
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create test session');

      setResult(data);
      fetchTestAccounts();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleReset = async (companyId, ownerEmail) => {
    setActionLoading(companyId);
    try {
      // Use the admin-delete-company endpoint to clean up, then create fresh session
      const delResponse = await fetch(`${API_BASE}/api/admin-delete-company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId,
          adminEmail,
          purgeOwner: true
        })
      });

      if (!delResponse.ok) {
        const errData = await delResponse.json();
        throw new Error(errData.error || 'Failed to delete company');
      }

      // Create fresh test session with same email
      const createResponse = await fetch(`${API_BASE}/api/test-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: ownerEmail,
          password,
          adminEmail
        })
      });

      const data = await createResponse.json();
      if (!createResponse.ok) throw new Error(data.error || 'Failed to recreate test session');

      setResult(data);
      fetchTestAccounts();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (companyId) => {
    setActionLoading(companyId);
    try {
      const response = await fetch(`${API_BASE}/api/admin-delete-company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId,
          adminEmail,
          purgeOwner: true
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to delete');
      }

      fetchTestAccounts();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1000px', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
        <FlaskConical size={28} color="#e040fb" />
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#e0e0e0', margin: 0 }}>
            Test Accounts
          </h1>
          <p style={{ color: '#888', margin: 0, fontSize: '0.85rem' }}>
            Create test accounts to walk through the signup flow without hitting Stripe
          </p>
        </div>
      </div>

      {/* Create Form */}
      <div style={{
        background: '#1a1a1a',
        border: '1px solid #2a2a2a',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ color: '#e0e0e0', marginTop: 0, marginBottom: '1rem', fontSize: '1rem' }}>
          Create Test Account
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', color: '#888', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#2a2a2a',
                border: '1px solid #3a3a3a',
                borderRadius: '8px',
                color: '#e0e0e0',
                fontSize: '0.9rem'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', color: '#888', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
              Password
            </label>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#2a2a2a',
                border: '1px solid #3a3a3a',
                borderRadius: '8px',
                color: '#e0e0e0',
                fontSize: '0.9rem'
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', color: '#888', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
            Affiliate Ref (optional)
          </label>
          <input
            type="text"
            value={affiliateRef}
            onChange={(e) => setAffiliateRef(e.target.value)}
            placeholder="e.g., treadway"
            style={{
              width: '100%',
              maxWidth: '300px',
              padding: '0.75rem',
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: '#e0e0e0',
              fontSize: '0.9rem'
            }}
          />
        </div>

        <button
          onClick={handleCreate}
          disabled={creating || !email}
          style={{
            padding: '0.75rem 1.5rem',
            background: creating ? '#666' : '#e040fb',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: creating ? 'not-allowed' : 'pointer'
          }}
        >
          {creating ? 'Creating...' : 'Create Test Account'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: '#2a1a1a',
          border: '1px solid #e74c3c',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: '#e74c3c'
        }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Result */}
      {result && (
        <div style={{
          background: '#1a2a1a',
          border: '1px solid #4ecca3',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ color: '#4ecca3', marginTop: 0, marginBottom: '1rem', fontSize: '1rem' }}>
            Test Account Ready
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {/* Email */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ color: '#888', width: '80px', fontSize: '0.85rem' }}>Email:</span>
              <code style={{
                background: '#2a2a2a',
                padding: '0.4rem 0.75rem',
                borderRadius: '6px',
                color: '#e0e0e0',
                fontSize: '0.9rem',
                flex: 1
              }}>
                {result.email}
              </code>
              <button
                onClick={() => copyToClipboard(result.email, 'email')}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: copied === 'email' ? '#4ecca3' : '#888' }}
              >
                {copied === 'email' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>

            {/* Password */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ color: '#888', width: '80px', fontSize: '0.85rem' }}>Password:</span>
              <code style={{
                background: '#2a2a2a',
                padding: '0.4rem 0.75rem',
                borderRadius: '6px',
                color: '#e0e0e0',
                fontSize: '0.9rem',
                flex: 1
              }}>
                {result.password}
              </code>
              <button
                onClick={() => copyToClipboard(result.password, 'password')}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: copied === 'password' ? '#4ecca3' : '#888' }}
              >
                {copied === 'password' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>

            {/* Login URL */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ color: '#888', width: '80px', fontSize: '0.85rem' }}>Login URL:</span>
              <a
                href={result.loginUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: '#2a2a2a',
                  padding: '0.4rem 0.75rem',
                  borderRadius: '6px',
                  color: '#4ecca3',
                  fontSize: '0.9rem',
                  flex: 1,
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {result.loginUrl} <ExternalLink size={14} />
              </a>
              <button
                onClick={() => copyToClipboard(result.loginUrl, 'url')}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: copied === 'url' ? '#4ecca3' : '#888' }}
              >
                {copied === 'url' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </div>

          {/* Quick open buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <a
              href={result.loginUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '0.5rem 1rem',
                background: '#4ecca3',
                borderRadius: '6px',
                color: '#0a0a0a',
                fontSize: '0.85rem',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}
            >
              <ExternalLink size={14} /> Open Test Signup
            </a>
          </div>
        </div>
      )}

      {/* Active Test Accounts Table */}
      <div style={{
        background: '#1a1a1a',
        border: '1px solid #2a2a2a',
        borderRadius: '12px',
        padding: '1.5rem'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem'
        }}>
          <h3 style={{ color: '#e0e0e0', margin: 0, fontSize: '1rem' }}>
            Active Test Accounts
          </h3>
          <button
            onClick={fetchTestAccounts}
            style={{
              background: 'transparent',
              border: '1px solid #3a3a3a',
              borderRadius: '6px',
              padding: '0.4rem 0.75rem',
              color: '#888',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: '0.8rem'
            }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {loadingAccounts ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '2rem' }}>Loading...</p>
        ) : testAccounts.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>
            No active test accounts
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem', color: '#888', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>Company</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', color: '#888', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', color: '#888', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>Tier</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', color: '#888', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>Created</th>
                <th style={{ textAlign: 'right', padding: '0.5rem', color: '#888', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {testAccounts.map(account => (
                <tr key={account.id} style={{ borderBottom: '1px solid #1a1a1a' }}>
                  <td style={{ padding: '0.75rem 0.5rem', color: '#e0e0e0', fontSize: '0.9rem' }}>
                    {account.name}
                    {account.business_type && (
                      <span style={{ color: '#666', fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                        ({account.business_type})
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', color: '#888', fontSize: '0.85rem' }}>
                    {account.ownerEmail}
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    <span style={{
                      background: '#2a2a2a',
                      color: '#ff6b35',
                      padding: '0.2rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {account.tier}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', color: '#666', fontSize: '0.8rem' }}>
                    {new Date(account.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleReset(account.id, account.ownerEmail)}
                        disabled={actionLoading === account.id}
                        style={{
                          background: 'transparent',
                          border: '1px solid #3a3a3a',
                          borderRadius: '6px',
                          padding: '0.35rem 0.6rem',
                          color: '#4ecca3',
                          cursor: actionLoading === account.id ? 'not-allowed' : 'pointer',
                          fontSize: '0.8rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem'
                        }}
                      >
                        <RefreshCw size={12} /> Reset
                      </button>
                      <button
                        onClick={() => handleDelete(account.id)}
                        disabled={actionLoading === account.id}
                        style={{
                          background: 'transparent',
                          border: '1px solid #3a3a3a',
                          borderRadius: '6px',
                          padding: '0.35rem 0.6rem',
                          color: '#e74c3c',
                          cursor: actionLoading === account.id ? 'not-allowed' : 'pointer',
                          fontSize: '0.8rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem'
                        }}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
